import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { StructuredDiffPayload } from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import { ChatRequestApi } from "~/functions/api/chatRequest";
import { KnowledgeApi } from "~/functions/api/knowledge";
import type { Knowledge } from "~/interface/knowledge";
import type { GenerateKnowledge } from "~/schema/knowledge";
import { AIChatApi } from "../../functions/api/aiChat";
import { useOrganization } from "../../hooks/useOrganization";
import { useProjectPermission } from "../../hooks/useProjectPermission";
import { resetModeSession } from "../../slices/projectSlice";
import type { AppDispatch, RootState } from "../../store";
import ChatHeader from "./components/ChatHeader";
import ChatInput from "./components/ChatInput";
import ChatMessage from "./components/ChatMessage";
import ChatSuggestion from "./components/ChatSuggestion";
import KnowledgeSuggestion from "./components/knowledgeSuggestion";
import EnhancedReminiscenceModal from "./EnhancedReminiscenceModal";
import styles from "./styles.module.scss";
import type { ChatMessage as ChatMessageType, GenerationMode } from "./types";
import type { AIChatDocumentType } from "./types/documentType";
import { generateMessageId, loadMessages } from "./utils/messageHandlers";
import { createChunkHandler, finalizeMessage } from "./utils/streamingHandlers";
import { getNextMessageTimestamp } from "./utils/timestampHelpers";
import { resumeMonitoring } from "./utils/workflowMonitor";

export type AIChatHandle = {
  loadThread: (thread: { id?: string; threadId?: string }, mode?: GenerationMode) => Promise<void>;
};

interface AIChatProps {
  isOpen: boolean;
  onClose: () => void;
  onPreviewDiff?: (
    before: string,
    after: string,
    structuredDiff?: StructuredDiffPayload | null,
  ) => void;
  height?: string;
  maxHeight?: string;
  fluid?: boolean;
  minWidth?: number;
  currentDocumentId: string;
  currentProjectId: string;
  currentDocumentType: AIChatDocumentType;
  originalDocument: string;
  threadId?: string;
  resourceId?: string;
  onOpenHistory?: () => void;
  onThreadUpdate?: () => void;
  onThreadChange?: (threadId: string) => void;
}

const AIChat = React.forwardRef<AIChatHandle, AIChatProps>(
  (
    {
      isOpen,
      onClose,
      onPreviewDiff,
      height = "calc(100vh - 200px)",
      maxHeight = "600px",
      fluid = false,
      minWidth = 300,
      currentProjectId,
      currentDocumentId,
      currentDocumentType,
      originalDocument,
      threadId,
      resourceId,
      onOpenHistory,
      onThreadUpdate,
      onThreadChange,
    },
    ref,
  ) => {
    const dispatch = useDispatch<AppDispatch>();
    // 基本的な状態管理
    const [messages, setMessages] = useState<ChatMessageType[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const [streamingContent, setStreamingContent] = useState<string>("");
    const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
    const [editMode, setEditMode] = useState<boolean>(() => {
      const saved = localStorage.getItem("reqxign_aiChatEditMode");
      return saved !== null ? saved === "true" : true;
    });
    const generationMode: GenerationMode = "agent";
    // サーバ側スレッド作成の遅延実行を管理（作成済みキーを保持）
    const createdThreadsRef = useRef<Set<string>>(new Set());
    // handleNewChat直後の自動読み込みを抑制するフラグ
    const isNewChatRef = useRef(false);
    // タイムアウト管理
    const [timeoutMessage, setTimeoutMessage] = useState<string>("");
    const streamingStartTime = useRef<number | null>(null);
    const timeoutCheckInterval = useRef<NodeJS.Timeout | null>(null);

    // 構造化出力のデータを保持するstate
    const [updatedContent, setUpdatedContent] = useState<string | null>(null);
    const [structuredDiff, setStructuredDiff] = useState<StructuredDiffPayload | null>(null);

    // 修正待ち状態管理

    // モーダル表示
    const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);

    // ナレッジサジェスチョン表示管理
    const [showKnowledgeSuggestion, setShowKnowledgeSuggestion] = useState(false);
    const [knowledgeGenerating, setKnowledgeGenerating] = useState(false);

    // スレッドの存在を追跡するstate
    const [hasThreads, setHasThreads] = useState<boolean>(false);

    // 送信ごとに残数をローカルで即時反映するためのオフセット（Redux storeは更新しない）
    const [localRemainingOffset, setLocalRemainingOffset] = useState(0);

    // UI制御
    const [chatWidth, setChatWidth] = useState(() => {
      const savedWidth = localStorage.getItem("aiChatWidth");
      return savedWidth ? Math.max(parseInt(savedWidth, 10), minWidth) : minWidth;
    });
    const [isResizing, setIsResizing] = useState(false);
    const latestWidthRef = useRef<number>(0);
    useEffect(() => {
      latestWidthRef.current = chatWidth;
    }, [chatWidth]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContentRef = useRef<HTMLDivElement>(null);

    // 選択されたスレッド（送信先）をローカルで保持
    const [activeThreadId, setActiveThreadId] = useState<string | undefined>(threadId);
    const { organization, currentUser, currentProject } = useSelector(
      (state: RootState) => state.auth,
    );
    const { currentSession } = useSelector((state: RootState) => state.project);
    const { refresh: refreshOrganization } = useOrganization(organization.id);
    const api = new AIChatApi();
    const { canAccessChat } = useProjectPermission();

    // 現在のgenerationModeに基づいてresourceIdを動的に生成
    const getCurrentResourceId = () => {
      if (!currentProject?.id || !currentUser?.id || !currentDocumentId) return resourceId;
      return `${currentProject.id}${currentDocumentId}${currentUser.id}`;
    };

    // スレッドを既存として登録（重複作成を防ぐ）
    const markThreadAsExisting = (tid?: string) => {
      const agentId = "commonAgent";
      const rid = getCurrentResourceId();
      if (!rid || !tid) return;
      const key = `${agentId}:${rid}:${tid}`;
      createdThreadsRef.current.add(key);
    };

    // 必要なときだけスレッドを作成（初回送信時）
    // 戻り値: 新規作成した場合は true、既存スレッドの場合は false
    const createThread = async (rid?: string, tid?: string): Promise<boolean> => {
      const agentId = "commonAgent";
      if (!rid || !tid) return false;
      const key = `${agentId}:${rid}:${tid}`;
      if (createdThreadsRef.current.has(key)) return false;
      try {
        const api = new AIChatApi();
        await api.createThread(agentId, rid, tid);
        createdThreadsRef.current.add(key);
        return true; // 新規作成成功
      } catch (e) {
        console.error("[AIChat] ensureThreadCreated error:", e);
        throw e; // エラーは呼び出し元で処理
      }
    };

    const loadThreadMessages = useCallback(
      async (thread: { id?: string; threadId?: string }) => {
        try {
          const api = new AIChatApi();
          const tid = thread?.id || thread?.threadId;
          if (!tid) return;

          const result = await loadMessages(thread, generationMode, api);
          setMessages(result);
          setActiveThreadId(tid);
          setHasThreads(true);

          // 既存スレッドとして登録（重複作成を防ぐ）
          markThreadAsExisting(tid);

          // 親コンポーネントにスレッド変更を通知
          if (tid) {
            onThreadChange?.(tid);
          }
        } catch (e) {
          console.error("[AIChat] load thread messages error:", e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- markThreadAsExisting only mutates a ref and doesn't need to trigger re-renders
      },
      [
        onThreadChange, // 既存スレッドとして登録（重複作成を防ぐ）
        markThreadAsExisting,
      ],
    );

    // Expose imperative API to parent (for external history sidebar)
    React.useImperativeHandle(
      ref,
      () => ({
        loadThread: async (thread: { id?: string; threadId?: string }) => {
          const tid = thread?.id || thread?.threadId;
          if (!tid) return;

          // 履歴からの切り替え時も状態をリセット
          setActiveThreadId(tid);
          setStreamingMessageId(null);
          setStreamingContent("");
          setUpdatedContent(null);
          setStructuredDiff(null);
          setLastFailedMessage(null);
          setInputValue("");
          setTimeoutMessage("");
          // ナレッジサジェスチョンの状態もリセット
          setShowKnowledgeSuggestion(false);
          await loadThreadMessages(thread);
        },
      }),
      [loadThreadMessages],
    );

    // generateMessageIdはutils/messageHandlers.tsからインポート済み

    // 新しいチャットセッションを開始
    const handleNewChat = async () => {
      // 自動読み込みを抑制
      isNewChatRef.current = true;
      // 現在のドキュメントIDで新規セッションを開始し、作成したスレッドIDを取得
      const action = await dispatch(resetModeSession(currentDocumentId));
      const payload =
        action && (action as { payload?: { currentSession?: { threadId?: string } } }).payload;
      const newThreadId = payload?.currentSession?.threadId as string | undefined;
      if (newThreadId) {
        setActiveThreadId(newThreadId);
        // 親コンポーネントにスレッド変更を通知
        onThreadChange?.(newThreadId);
      }
      // 注意: createThread API呼び出しはメッセージ送信時に初めて実行される
      // メッセージをクリア
      setMessages([]);
      // 入力フィールドをクリア
      setInputValue("");
      // その他の状態をリセット
      setStreamingMessageId(null);
      setStreamingContent("");
      setUpdatedContent(null);
      setStructuredDiff(null);
      setLastFailedMessage(null);
      setTimeoutMessage("");
      // ナレッジサジェスチョンの状態もリセット
      setShowKnowledgeSuggestion(false);
      // スレッドの存在状態を再確認
      const checkThreads = async () => {
        try {
          const rid = getCurrentResourceId();
          if (!rid) {
            setHasThreads(false);
            return;
          }
          const api = new AIChatApi();
          const agentId = "commonAgent";
          const list = await api.getThreads(agentId, rid, 10);

          const arr = Array.isArray(list) ? list : [];
          setHasThreads(arr.length !== 0);
        } catch (e) {
          console.error("[AIChat] Failed to check threads:", e);
          setHasThreads(false);
        }
      };
      checkThreads();
    };

    // handleStreamingChunkはutils/streamingHandlers.tsからインポート済み
    // エラー処理のための追加ロジック
    const handleStreamingChunk = (aiMessageId: string) => {
      const baseHandler = createChunkHandler(aiMessageId, setMessages, {
        setStreamingContent,
        setStructuredDiff,
      });
      return (chunk: { type?: string }) => {
        // エラー処理時の追加ロジック
        if (chunk?.type === "error") {
          setStreamingMessageId(null);
          const lastUserMessage = messages.findLast((msg) => msg.sender === "user");
          if (lastUserMessage) {
            setLastFailedMessage(lastUserMessage.content);
            setInputValue(lastUserMessage.content);
          }
        }
        baseHandler(chunk);
      };
    };

    // propsのthreadIdが変更された時にactiveThreadIdを更新
    useEffect(() => {
      if (threadId) {
        setActiveThreadId(threadId);
      }
    }, [threadId]);

    // AIチャットを閉じた時にタイムアウトメッセージをクリア
    useEffect(() => {
      if (!isOpen) {
        setTimeoutMessage("");
      }
    }, [isOpen]);

    // タイムアウトチェック用の関数
    useEffect(() => {
      if (streamingMessageId) {
        // ストリーミング開始時に開始時刻を記録
        if (!streamingStartTime.current) {
          streamingStartTime.current = Date.now();
        }

        // タイムアウトチェック用のインターバルを設定（1秒ごと）
        timeoutCheckInterval.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - (streamingStartTime.current || 0)) / 1000);

          // 2分（120秒）経過したらタイムアウト
          if (elapsed >= 120) {
            setTimeoutMessage("タイムアウトしました。リロードしてください");

            // インターバルをクリア
            if (timeoutCheckInterval.current) {
              clearInterval(timeoutCheckInterval.current);
              timeoutCheckInterval.current = null;
            }

            // ストリーミングを停止
            setStreamingMessageId(null);
            setStreamingContent("");
            streamingStartTime.current = null;
          }
        }, 1000);
      } else {
        // ストリーミング終了時にクリーンアップとタイムアウトメッセージをクリア
        streamingStartTime.current = null;
        if (timeoutCheckInterval.current) {
          clearInterval(timeoutCheckInterval.current);
          timeoutCheckInterval.current = null;
        }
        // ストリーミングが正常終了した場合はタイムアウトメッセージをクリア
        setTimeoutMessage("");
      }

      return () => {
        if (timeoutCheckInterval.current) {
          clearInterval(timeoutCheckInterval.current);
          timeoutCheckInterval.current = null;
        }
      };
    }, [streamingMessageId]);

    // 送信のコア処理（共通処理）
    const sendHandler = async (
      text: string,
      options?: {
        // ユーザーメッセージを履歴に追加するか（承認などシステム操作のみの場合はfalse）
        addUserMessage?: boolean;
      },
    ) => {
      if (!text || text.length > 10000) return;
      const addUserMessage = options?.addUserMessage ?? true;

      // タイムアウトメッセージをクリア
      setTimeoutMessage("");

      // 必要に応じてユーザーメッセージを追加（画面表示は message フィールドのみ）
      if (addUserMessage) {
        const userMessage: ChatMessageType = {
          id: generateMessageId("user"),
          content: text,
          sender: "user",
          timestamp: getNextMessageTimestamp(messages),
        };
        setMessages((prev) => [...prev, userMessage]);
      }

      // チャットリクエストを消費（クレジットチェック + JWT取得）
      try {
        const chatRequestApi = new ChatRequestApi();
        const consume = await chatRequestApi.consume(organization.id, currentProjectId);
        if (!consume) {
          setMessages((prev) => {
            const aiErr: ChatMessageType = {
              id: generateMessageId("ai"),
              content:
                "AI利用上限に達しているか、リクエスト処理に失敗しました。時間をおいて再度お試しください。",
              sender: "assistant",
              timestamp: getNextMessageTimestamp(prev),
            };
            return [...prev, aiErr];
          });
          return;
        }
        // 成功したのでローカルの残りリクエスト数オフセットを-1（Redux storeは更新せず再レンダリングを防止）
        setLocalRemainingOffset((prev) => prev - 1);
      } catch (_e) {
        setMessages((prev) => {
          const aiErr: ChatMessageType = {
            id: generateMessageId("ai"),
            content:
              "チャットリクエストの消費に失敗しました。ネットワーク状況を確認し、再度お試しください。",
            sender: "assistant",
            timestamp: getNextMessageTimestamp(prev),
          };
          return [...prev, aiErr];
        });
        return;
      }

      // AIメッセージの準備
      const aiMessageId = generateMessageId("ai");

      setMessages((prev) => {
        const aiMessage: ChatMessageType = {
          id: aiMessageId,
          content: "",
          sender: "assistant",
          timestamp: getNextMessageTimestamp(prev),
          generationMode,
          originalUserText: text,
          toolCalls: [],
        };
        return [...prev, aiMessage];
      });
      setStreamingMessageId(aiMessageId);
      setStreamingContent("");

      // スレッド作成フラグ
      let isNewThread = false;

      try {
        const currentResourceId = getCurrentResourceId();

        if (!activeThreadId || !threadId || !currentResourceId) {
          console.error("[AIChat][sendHandler] agent mode requires activeThreadId and threadId");
          return;
        }

        // スレッドを作成（新規チャット作成時のみ）
        try {
          isNewThread = await createThread(currentResourceId, activeThreadId);
          // 新規スレッド作成成功時に履歴を更新（リロードボタンと同じ処理）
          if (isNewThread) {
            onThreadUpdate?.();
          }
        } catch (createThreadError) {
          console.error("[AIChat] Failed to create thread:", createThreadError);
          // スレッド作成失敗時はクレジットを返却して早期リターン
          try {
            const chatRequestApi = new ChatRequestApi();
            await chatRequestApi.failure(organization.id, currentProjectId);
          } catch (returnError) {
            console.error("[AIChat] Failed to return credit:", returnError);
          }
          refreshOrganization();
          // エラーメッセージを設定
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId
                ? (() => {
                    const { updatedContent: _u, structuredDiff: _s, ...rest } = msg;
                    return {
                      ...rest,
                      content: "スレッドの作成に失敗しました。再度お試しください。",
                      isError: true as const,
                    };
                  })()
                : msg,
            ),
          );
          return; // 早期リターン → streamWorkflowは呼ばれない
        }

        // agentモードでstreamAgentWorkflowを呼び出し
        const runId = generateMessageId("workflow");

        const result = await api.streamWorkflow(
          "agentWorkflow",
          text,
          currentDocumentId,
          currentProjectId,
          activeThreadId,
          currentResourceId,
          runId,
          editMode,
          handleStreamingChunk(aiMessageId),
          organization.id, // organizationId - クレジット返却に必要
          currentUser.id, // userId - クレジット返却に必要
        );

        // AIメッセージを更新してagentモードの結果を表示
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId
              ? {
                  ...msg,
                  content:
                    result?.message || "エラーが発生しました。時間をおいて再度お試しください。",
                  generationMode: "agent",
                }
              : msg,
          ),
        );

        // streamWorkflow が null を返した場合はクレジット返還後のため Redux を同期
        if (result === null) {
          refreshOrganization();
        }

        // ストリーミング完了後の処理
        finalizeMessage(aiMessageId, streamingContent, updatedContent, structuredDiff, setMessages);
        if (updatedContent) {
          setUpdatedContent(updatedContent);
        }
        if (result?.structuredDiff) {
          setStructuredDiff(result.structuredDiff);
        }
      } catch (error) {
        // エラー時はメッセージを更新（ストリーム側で既に日本語メッセージを出している場合はそれを優先）
        // 注: クレジット返却はstreamWorkflow内部で処理される
        const raw = error instanceof Error ? error.message : "エラーが発生しました";
        const prefer =
          streamingContent && streamingContent.length > 0 ? streamingContent : `エラー: ${raw}`;
        setMessages((prev) =>
          prev
            .map((msg) => {
              if (msg?.id !== aiMessageId) return msg;
              const existing = msg.toolCalls || [];
              const { updatedContent: _u, structuredDiff: _s, ...rest } = msg;
              return { ...rest, content: prefer, toolCalls: existing, isError: true as const };
            })
            .filter(Boolean),
        );
        // エラー発生時は送信テキストを保持して再送信可能にする
        setLastFailedMessage(text);
        setInputValue(text);
        refreshOrganization();
      } finally {
        setStreamingMessageId(null);
        setStreamingContent("");
        setUpdatedContent(null);
        setStructuredDiff(null);

        // メッセージ送信完了を親コンポーネントに通知
        // 新規スレッド作成時かつユーザーメッセージを追加した場合のみ更新
        if (options?.addUserMessage && isNewThread) {
          onThreadUpdate?.();
        }
      }
    };

    // メッセージ送信処理（入力欄の送信ボタン）
    const handleSend = async () => {
      const text = inputValue.trim();
      if (!text || text.length > 10000) return;
      // 送信直後にインプットを即時クリア
      setInputValue("");
      // エラー状態をクリア
      setLastFailedMessage(null);
      await sendHandler(text);
    };

    // エラーメッセージの再送はメッセージ欄では提供せず、入力欄から行う
    // スクロール制御: メッセージ追加・ストリーミング更新時に最下部へスクロール
    useEffect(() => {
      if (chatContentRef.current) {
        chatContentRef.current.scrollTo({
          top: chatContentRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, [messages, streamingContent]);

    // ReduxのthreadId変更に追随（新規セッション開始など）
    useEffect(() => {
      setActiveThreadId(threadId);
      // スレッド変更時は状態をリセット（前のスレッドの状態をクリア）
      setStreamingMessageId(null);
      setStreamingContent("");
      setUpdatedContent(null);
      setStructuredDiff(null);
      setLastFailedMessage(null);
      setTimeoutMessage("");
      // ナレッジサジェスチョンの状態もリセット
      setShowKnowledgeSuggestion(false);
      if (threadId) {
        setHasThreads(true);
      }
    }, [threadId]);

    // AIChat が開かれた時に最新スレッドを自動読み込み（初回オープン時のみ）
    const hasInitialLoadRef = useRef(false);
    useEffect(() => {
      if (!isOpen) {
        // 閉じた時にフラグをリセット（次回オープン時に再読み込みを許可）
        hasInitialLoadRef.current = false;
        isNewChatRef.current = false;
        return;
      }
      // 新規チャット直後 or 既に初回読み込み済みならスキップ
      if (isNewChatRef.current || hasInitialLoadRef.current) return;
      if (messages.length > 0) {
        hasInitialLoadRef.current = true;
        return;
      }
      hasInitialLoadRef.current = true;

      const loadLatestThread = async () => {
        try {
          const rid = getCurrentResourceId();
          if (!rid) {
            setHasThreads(false);
            return;
          }

          const api = new AIChatApi();
          const agentId = "commonAgent";
          const list = await api.getThreads(agentId, rid, 10);
          const arr = Array.isArray(list) ? list : [];

          const hasThreads = arr.length !== 0;
          setHasThreads(hasThreads);

          if (hasThreads) {
            const sorted = [...arr].sort((a, b) => {
              const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
              const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
              return tb - ta;
            });
            if (sorted[0]) await loadThreadMessages(sorted[0]);
          }
        } catch (e) {
          console.error("[AIChat] Failed to load latest thread:", e);
          setHasThreads(false);
        }
      };

      loadLatestThread();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // ワークフロー監視のためのState
    const [workflowWatcher, setWorkflowWatcher] = useState<NodeJS.Timeout | null>(null);

    // ワークフロー監視関連の関数はutils/workflowMonitor.tsに移動済み

    // チャットモーダルが開かれた際の処理
    useEffect(() => {
      if (isOpen && activeThreadId && !workflowWatcher) {
        resumeMonitoring(
          isOpen,
          activeThreadId,
          messages,
          currentSession,
          getCurrentResourceId,
          api,
          generationMode,
          setMessages,
          setStreamingMessageId,
          generateMessageId,
        );
      }

      // モーダルが閉じられた際に監視を停止
      if (!isOpen && workflowWatcher) {
        clearInterval(workflowWatcher);
        setWorkflowWatcher(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- messages, api, getCurrentResourceId はトリガーではない。messages を含めると resumeMonitoring → setMessages → 再発火で無限ループになる
    }, [isOpen, activeThreadId]);

    // コンポーネントのアンマウント時にワークフロー監視を停止
    useEffect(() => {
      return () => {
        if (workflowWatcher) {
          clearInterval(workflowWatcher);
          setWorkflowWatcher(null);
        }
      };
    }, [workflowWatcher]);

    // ナレッジ生成の可否チェック（2往復以上のAIメッセージが必要 + 権限チェック）
    const canGenerateKnowledge = () => {
      const aiMessages = messages.filter(
        (msg) => msg.sender === "assistant" && !msg.isError,
      ).length;
      return aiMessages >= 2;
    };

    const handleKnowledgeGenerate = () => {
      if (!canGenerateKnowledge()) return;
      setShowKnowledgeSuggestion(true);
    };

    // ナレッジサジェスチョンの承認
    const handleAcceptKnowledgeSuggestion = async () => {
      setKnowledgeGenerating(true);
      if (!canGenerateKnowledge()) return;
      if (!activeThreadId || !threadId || !resourceId)
        throw new Error(
          "スレッドが存在しません。プロジェクトトップ画面からページを開き直してください。",
        );

      try {
        const aiApi = new AIChatApi();
        const knowledgeApi = new KnowledgeApi();
        const knowledge: GenerateKnowledge = await aiApi.createKnowledge(activeThreadId, 10);

        if (!knowledge || !knowledge.title || !knowledge.meta_prompt) {
          console.error("ナレッジ生成に失敗しました: 不正なレスポンス", knowledge);
          return;
        }

        const addedKnowledge: Knowledge | null = await knowledgeApi.create(
          currentUser.organization_id,
          currentProject.id,
          {
            type: currentDocumentType,
            title: knowledge.title,
            meta_prompt: knowledge.meta_prompt,
            created_at: new Date().toISOString(),
          },
        );

        // ナレッジの保存に成功した場合
        if (addedKnowledge) {
          // ナレッジメッセージを作成
          const messageContent = `タイトル: ${knowledge.title}\nメタプロンプト: ${knowledge.meta_prompt}`;
          const result = await aiApi.createMessage(
            messageContent,
            addedKnowledge.id,
            activeThreadId,
            resourceId,
          );
          if (!result) {
            console.error("ナレッジメッセージの保存に失敗しました");
            return;
          }

          // UIにナレッジメッセージを即座に追加
          setMessages((prev) => {
            const knowledgeMessage: ChatMessageType = {
              id: `system_${Date.now()}`,
              content: messageContent,
              sender: "system",
              timestamp: getNextMessageTimestamp(prev),
              generationMode: generationMode,
              metadata: {
                knowledgeId: addedKnowledge.id,
              },
            };
            return [...prev, knowledgeMessage];
          });

          // ナレッジサジェスチョンを閉じる
          setShowKnowledgeSuggestion(false);
        } else {
          console.error("ナレッジ生成に失敗しました");
        }
      } catch (error) {
        console.error("ナレッジ生成エラー:", error);
      } finally {
        setKnowledgeGenerating(false);
      }
    };

    // ナレッジサジェスチョンの閉じる
    const handleRejectKnowledgeSuggestion = () => {
      setShowKnowledgeSuggestion(false);
      setKnowledgeGenerating(false);
    };

    // 編集モード変更ハンドラー（localStorageに保存）
    const handleEditModeChange = (newValue: boolean) => {
      setEditMode(newValue);
      localStorage.setItem("reqxign_aiChatEditMode", String(newValue));
    };

    // リサイズハンドラー
    const handleResizeStart = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const handleMouseMove = (e: MouseEvent) => {
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= minWidth && newWidth <= 800) {
          setChatWidth(newWidth);
          latestWidthRef.current = newWidth;
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        const finalWidth = latestWidthRef.current || chatWidth;
        localStorage.setItem("aiChatWidth", finalWidth.toString());
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };

    if (!isOpen) return null;

    // 権限チェック：contributorまたはownerでない場合はチャットを表示しない
    if (!canAccessChat()) {
      return null;
    }

    const wrapperStyle: React.CSSProperties = fluid
      ? { height, maxHeight, width: "100%", maxWidth: "none" }
      : { height, maxHeight, width: `${chatWidth}px` };

    return (
      <div className={styles["chatWrapper"]} style={wrapperStyle}>
        <div
          className={`${styles["resizeHandle"]} ${isResizing ? styles["resizing"] : ""}`}
          onMouseDown={handleResizeStart}
        />
        <div className={styles["chatContainer"]}>
          <ChatHeader
            onClose={onClose}
            onHistory={onOpenHistory ?? (() => {})}
            onNewChat={handleNewChat}
            timeoutMessage={timeoutMessage}
            remainingOffset={localRemainingOffset}
            onRemainingOffsetReset={() => setLocalRemainingOffset(0)}
          />

          <div className={styles["chatContent"]} ref={chatContentRef}>
            <div className={styles["messagesContainer"]}>
              {/* サジェスチョン表示条件:
                メッセージがない AND スレッドが存在しない → サジェスチョンを表示
                メッセージがある OR スレッドが存在する → サジェスチョンを表示しない */}
              {messages.length === 0 && !hasThreads && (
                <ChatSuggestion
                  documentType={currentDocumentType}
                  onSuggestionClick={(suggestion) => {
                    setInputValue(suggestion);
                    // サジェスチョンクリック後、自動送信する場合は以下を有効化
                    // handleSend();
                  }}
                />
              )}

              {messages
                .filter((message) => message?.id)
                .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
                .map((message) => (
                  <ChatMessage
                    key={message.id}
                    projectId={currentProjectId}
                    message={message}
                    originalDocument={originalDocument}
                    onPreviewClick={() => {
                      try {
                        // 更新後ドキュメント: AIメッセージのupdatedContent
                        const before = originalDocument;
                        const after = message.updatedContent;
                        if (after && after !== before)
                          onPreviewDiff?.(before, after, message.structuredDiff);
                      } catch (e) {
                        console.error("[AIChat] onPreviewDiff error:", e);
                      }
                    }}
                    isStreaming={streamingMessageId === message.id}
                    {...(streamingMessageId === message.id && streamingContent
                      ? { streamingContent }
                      : {})}
                  />
                ))}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* ナレッジサジェスチョン表示（チャットインプット上部） */}
          {showKnowledgeSuggestion && (
            <div className={styles["knowledgeSuggestionWrapper"]}>
              <KnowledgeSuggestion
                onAccept={handleAcceptKnowledgeSuggestion}
                onReject={handleRejectKnowledgeSuggestion}
                isGenerating={knowledgeGenerating}
              />
            </div>
          )}

          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSend}
            disabled={!!streamingMessageId}
            isStreaming={!!streamingMessageId}
            hasError={!!lastFailedMessage}
            onKnowledgeAttach={() => setShowKnowledgeModal(true)}
            onKnowledgeGenerate={handleKnowledgeGenerate}
            knowledgeGenerateDisabled={!canGenerateKnowledge()}
            knowledgeGenerateTooltip={canGenerateKnowledge() ? "ナレッジを生成" : "チャット不足"}
            attachedGeneratedDocuments={[]}
            onRemoveGeneratedDocument={() => {}}
            editMode={editMode}
            onEditModeChange={handleEditModeChange}
            documentType={currentDocumentType}
          />

          {/* Character counter and hint outside ChatInput */}
          <div className={styles["inputFooter"]}>
            <div className={styles["characterCounter"]}>
              {inputValue.length > 3000 && (
                <span
                  className={
                    inputValue.length > 4000 ? styles["counterOverLimit"] : styles["counterNormal"]
                  }
                >
                  <strong>{inputValue.length}/4000</strong>
                </span>
              )}
            </div>
            <div className={styles["outerSendHint"]}>
              <span>
                <strong>Shift + Enter</strong> で改行
              </span>
            </div>
          </div>
        </div>

        <EnhancedReminiscenceModal
          open={showKnowledgeModal}
          onClose={() => setShowKnowledgeModal(false)}
          onSelect={() => setShowKnowledgeModal(false)}
        />
      </div>
    );
  },
);

export default AIChat;
